import React, { useState, useEffect, useCallback } from 'react';
import { Accordion, Card, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { LinkBtn, Icon } from '../../components';
import { camelCaseEmptyStringRecursive } from '../../services/utils';
import { showCourseRequest } from '../../requests/courses';
import basicCourse from './course';
import CourseParticipantsTable from './CourseParticipantsTable';
import EmployeeCourseIndex from '../EmployeeCourse/EmployeeCourseIndex';

const Field = ({ label, value, md = 3 }) => (
  <Col xs={12} md={md} className="mb-3 text-capitalize">
    <div className="d-flex flex-column">
      <span className="type">{label}</span>
      <span className={`answer ${!value ? 'text-grey' : ''}`}>{value || 'No Aplica'}</span>
    </div>
  </Col>
);

const CourseShow = ({ match }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [course, setCourse] = useState(basicCourse);
  const [onRequest, setOnRequest] = useState(true);

  const successCallback = useCallback(response => {
    const c = camelCaseEmptyStringRecursive(response.data);
    setCourse(c);
  }, []);

  const searchParams = new URLSearchParams(location.search);
  const accordionParam = searchParams.get('accordion');
  const modeParam = searchParams.get('mode');
  const [activeAccordion, setActiveAccordion] = useState(accordionParam || 'general');

  const fetchCourse = useCallback(() => {
    setOnRequest(true);
    showCourseRequest(match.params.id, {
      dispatch,
      successCallback,
      callback: () => setOnRequest(false)
    });
  }, [match.params.id, dispatch, successCallback]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCourse();
  }, [fetchCourse]);

  const {
    name,
    theme,
    courseAgency,
    activityType,
    translatedActivityType,
    courseModality,
    translatedCourseModality,
    maxAvailability,
    duration,
    limitDate,
    status,
    description,
    courseImage,
    courseCertificate,
    startDate,
    endDate,
    journeyDistribution,
    startTime,
    endTime,
    fullAddress,
    connectionLink,
    instructorsAttributes = [],
    costPerParticipant,
    parsedCostPerParticipant,
    parsedTotalValue,
    totalValue,
    senceCode,
    franchiseType,
    translatedFranchiseType,
    bipartiteCommittee,
    mandateIndicator
  } = course;

  const daysText = journeyDistribution || '—';
  const hasBanner = courseImage && (courseImage.fileUrl || courseImage.file_url);

  const isEditMode = modeParam === 'edit';

  return (
    <div className={`${onRequest && 'bg-opacity'} form-course mt-2`}>
      {onRequest && (
        <div className="containerSpinnerLoad fix-middle center-spinner position-fixed">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      <Row className="my-3 align-items-center ">
        <LinkBtn variant="circle-dark" className="btn-circle mt-3" to="/courses">
          <Icon icon="chevron-back" />
        </LinkBtn>
        <h2 className="text-uppercase ml-4 mt-4">{name}</h2>
      </Row>

      <div className="yellow-wrapper">
        <Accordion activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'general' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="general"
              className="card-header-title"
              onClick={() => setActiveAccordion(activeAccordion === 'general' ? null : 'general')}
              style={{ cursor: 'pointer' }}
            >
              <span>Datos Generales</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="general">
            <Card>
              <Card.Body>
                <Row>
                  <Field label="Nombre" value={name} />
                  <Field label="Tema" value={theme} />
                  <Field label="Empresa/Organismo que Imparte Capacitación" value={courseAgency} />
                  <Field label="Tipo de Actividad" value={translatedActivityType || activityType} />
                </Row>

                <Row>
                  <Field label="Modalidad" value={translatedCourseModality || courseModality} />
                  <Field label="Cupos Disponibles" value={maxAvailability} />
                  <Field label="Duración" value={duration} />
                  <Field label="Fecha Inscripción Límite" value={limitDate} />
                </Row>

                <Row>
                  <Field label="Tipo de Franquicia" value={translatedFranchiseType || franchiseType} />
                  <Field label="Estado" value={status === 'published' ? 'Publicado' : 'No Publicado'} />
                </Row>

                <Row>
                  <Field label="Descripción" md={12} value={description} />
                </Row>

                {/* banner */}
                <Row className="mt-2">
                  <Col xs={4} md={1} className="mb-3">
                    <div className="d-flex flex-column">
                      <span className="type">Banner del Curso</span>
                      {hasBanner ? (
                        <div className="custom-file-img mt-2">
                          <div className="content">
                            <img
                              src={courseImage.fileUrl || courseImage.file_url}
                              alt="Banner del curso"
                              className="img-fluid"
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="answer text-grey">No Aplica</span>
                      )}
                    </div>
                  </Col>

                  <Field
                    label="Certificado / Diploma a Entregar"
                    md={{ span: 4, offset: 2 }}
                    value={
                      courseCertificate?.fileUrl ? (
                        <a href={courseCertificate.fileUrl} target="_blank" rel="noopener noreferrer">
                          {courseCertificate.cleanFilename}
                        </a>
                      ) : (
                        'No Aplica'
                      )
                    }
                  />
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'dates' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="dates"
              className="card-header-title"
              onClick={() => setActiveAccordion(activeAccordion === 'dates' ? null : 'dates')}
              style={{ cursor: 'pointer' }}
            >
              <span>Fechas y Dirección de la Capacitación</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="dates">
            <Card>
              <Card.Body>
                <Row>
                  <Field label="Desde" md="2" value={startDate} />
                  <Field label="Hasta" md="2" value={endDate} />
                  <Field label="Días de Clases" value={daysText} />
                  <Field label="Hora Inicio" md="2" value={startTime} />
                  <Field label="Hora Término" md="2" value={endTime} />
                </Row>

                <Row>
                  <Field label="Dirección" md={6} value={fullAddress} />
                </Row>

                <Row>
                  <Field
                    label="Link de Conexión"
                    md={12}
                    value={
                      connectionLink ? (
                        <a href={connectionLink} target="_blank" rel="noopener noreferrer">
                          {connectionLink}
                        </a>
                      ) : (
                        'No Aplica'
                      )
                    }
                  />
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'instructors' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="instructors"
              className="card-header-title"
              onClick={() => setActiveAccordion(activeAccordion === 'instructors' ? null : 'instructors')}
              style={{ cursor: 'pointer' }}
            >
              <span>Instructor(es) / Relator(es)</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="instructors">
            <Card>
              <Card.Body>
                {instructorsAttributes.length === 0 && <p className="text-grey">No hay relatores</p>}

                {instructorsAttributes.map((inst, idx) => (
                  <div
                    key={inst.id || idx}
                    className="mb-4"
                    style={{
                      borderBottom: idx !== instructorsAttributes.length - 1 ? '1px solid #eee' : 'none'
                    }}
                  >
                    <Row>
                      <Field label="Nombre y Apellido" value={inst.name} />
                      <Field label="Tipo de Identificación" value={inst.identificationType} />
                      <Field label="N° Identificación" value={inst.nationalIdentification} />
                      <Field label="Cargo" className="text-capitalize" value={inst.position} />
                    </Row>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'cost' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="cost"
              className="card-header-title"
              onClick={() => setActiveAccordion(activeAccordion === 'cost' ? null : 'cost')}
              style={{ cursor: 'pointer' }}
            >
              <span>Costo / Sence</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="cost">
            <Card>
              <Card.Body>
                <Row>
                  <Field label="Costo por Participante" value={parsedCostPerParticipant || costPerParticipant} />
                  <Field label="Costo Total" value={parsedTotalValue || totalValue} />
                  <Field label="Código Sence" value={senceCode} />
                  <Field label="Indicador de Mandato" value={mandateIndicator} />
                </Row>

                <Row>
                  <Field label="Comité Bipartito" value={bipartiteCommittee ? 'Sí' : 'No'} />
                </Row>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-2" activeKey={activeAccordion} onSelect={k => setActiveAccordion(k)}>
          <Col className={`${activeAccordion === 'participants' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
            <Accordion.Toggle
              as={Col}
              eventKey="participants"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => setActiveAccordion(activeAccordion === 'participants' ? null : 'participants')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Participantes</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="participants">
            <Card>
              <Card.Body className={isEditMode ? 'px-0' : 'p-0'}>
                <div className="data-table-yellow px-2">
                  {isEditMode ? (
                    <EmployeeCourseIndex
                      onRequest={onRequest}
                      setOnRequest={setOnRequest}
                      course={course}
                      successCallback={successCallback}
                    />
                  ) : (
                    <CourseParticipantsTable employeeCourses={course.employeeCourses} />
                  )}
                </div>
              </Card.Body>
            </Card>
          </Accordion.Collapse>
        </Accordion>
      </div>
    </div>
  );
};

export default CourseShow;
